import { Box, Breadcrumb, HStack, StackProps } from '@chakra-ui/react'
import {
  AppPanelToggleButton,
  AppPanelToggleButtonProps,
} from '@src/components/sco/AppLayout/Panel/AppPanelToggleButton.tsx'
import { NextLink } from '@src/components/sco/utilities/NextLink'
import { BreadcrumbItem, useActiveContentContext } from '@src/contexts/ActiveContentContext'
import React, { Fragment } from 'react'

export type AppLayoutToolbarProps = {
  boxProps?: StackProps
  breadcrumbs?: BreadcrumbItem[]
  children?: React.ReactNode
  /** Flatter bar for dense shells (the admin toolbar): tighter padding on the `panel` surface. */
  compact?: boolean
  leftElements?: React.ReactNode
  rightElements?: React.ReactNode
  hideRightToggleButton?: boolean
  hideLeftToggleButton?: boolean
  hideBreadcrumbs?: boolean
  toggleButtonProps?: Partial<AppPanelToggleButtonProps>
}

export const AppLayoutToolbar: React.FC<AppLayoutToolbarProps> = props => {
  const activeContent = useActiveContentContext()

  return (
    <HStack
      align={'center'}
      backdropFilter={'blur(10px)'}
      bgColor={props.compact ? 'colorPalette.panel/90' : 'colorPalette.container/90'}
      borderBottom={'1px solid {colors.colorPalette.border/25}'}
      borderTopRadius={'md'}
      gap={3}
      justify={'stretch'}
      minHeight={'3.5rem'}
      position={'sticky'}
      top={0}
      width={'full'}
      zIndex={'docked'}
      {...(props.compact ? { paddingX: 1.5, paddingY: 0 } : { padding: 3 })}
      {...props.boxProps}
    >
      {props.children ? (
        props.children
      ) : (
        <>
          {!props.hideLeftToggleButton && (
            <AppPanelToggleButton panelId={'*-nav'} panelPosition={'left'} {...props.toggleButtonProps} />
          )}
          {/** Breadcrumbs */}
          {!props.hideBreadcrumbs && (
            <Box flexGrow={props.leftElements ? 0 : 1}>
              <Breadcrumb.Root fontSize={'sm'}>
                <Breadcrumb.List gap={1}>
                  {(props.breadcrumbs || activeContent.breadcrumbs || []).map(
                    (breadcrumb, index, breadcrumbs) => (
                      <Fragment key={`${breadcrumb.href}-${index}`}>
                        <Breadcrumb.Item>
                          <Breadcrumb.Link
                            asChild
                            _hover={{ color: 'colorPalette.fg', textDecoration: 'none' }}
                            fontWeight={'semibold'}
                            lineClamp={1}
                            color={
                              index === breadcrumbs.length - 1 ? 'colorPalette.fg/80' : 'colorPalette.fg/60'
                            }
                          >
                            <NextLink href={breadcrumb.href} title={breadcrumb.fullTitle}>
                              {breadcrumb.title || '...'}
                            </NextLink>
                          </Breadcrumb.Link>
                        </Breadcrumb.Item>
                        {index < breadcrumbs.length - 1 ? <Breadcrumb.Separator /> : null}
                      </Fragment>
                    )
                  )}
                </Breadcrumb.List>
              </Breadcrumb.Root>
            </Box>
          )}
          {props.leftElements}

          {props.rightElements}

          {/** Right Menu Icon */}
          {!props.hideRightToggleButton && activeContent.item?.id && (
            <HStack>
              <AppPanelToggleButton
                panelId={'*-aside'}
                panelPosition={'right'}
                {...props.toggleButtonProps}
              />
            </HStack>
          )}
        </>
      )}
    </HStack>
  )
}
